/** The one place the dashboard asks before spending Lux and says what was spent (rules in lib/luxSpend.ts).
 *
 *   const lux = useLuxSpend();
 *   if (!(await lux.confirm({ cost, label, balance }, event.currentTarget))) return;   // popover near the button
 *   … then lux.spent(toast) / lux.refunded(toast)                                      // the Lux toasts
 *
 * `LuxSpendProvider` (inside the router, main.tsx) holds the popover and the toast stack. The popover: the Lux
 * glyph, the cost, the balance after, "Don't ask me again before spending Lux" (saved on the user through
 * `PATCH /v1/me`) and where to switch it back; Esc cancels, Enter spends. Out of Lux it offers Buy Lux / Upgrade
 * instead (with the request's `returnTo`, checkout comes back to that page; `beforeLeave` runs first, e.g. to save the
page's unsaved work). Styles: `.lux-spend…` in styles.css (light and dark, reduced motion). */
import { useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent, type ReactNode } from "react";
import { FormattedMessage } from "react-intl";
import { Link, useNavigate } from "react-router-dom";
import { api, unwrap, type S } from "../api/client";
import { useMe } from "../api/org";
import { useIntlShape, useT, type Translate } from "../i18n/I18nProvider";
import { fmtLux, fmtMoney, fmtMoneyShort, type LuxWords } from "../lib/lux";
import { buyLuxHref, cannotPay, confirmCopy, needsConfirm, prepareLeave, pushToast, SETTINGS_PATH, toastText, type LuxToast, type SpendRequest } from "../lib/luxSpend";
import { LuxGlyph } from "./LuxIcon";

/** Lux & billing's words and numbers in the reader's language: `t`, the locale (for lib/lux.ts' `LuxWords`), and
 * Lux amounts, money (integer cents), dates and percentages formatted for it. */
export interface LuxFormat extends LuxWords {
  t: Translate;
  /** "42.50" in the locale (hundredths of a Lux). */
  lux: (hundredths: number) => string;
  /** "$1,234.50" from integer minor units. */
  money: (minor: number, currency?: string) => string;
  /** "$1,234" when whole, else with cents. */
  moneyShort: (minor: number, currency?: string) => string;
  /** "Oct 20, 2026" (with the time when asked); "—" for nothing. */
  date: (iso: string | null | undefined, withTime?: boolean) => string;
  /** A whole percent ("20 %" in French). */
  pct: (whole: number) => string;
  /** A count, grouped in the locale's way. */
  num: (n: number) => string;
}

export function useLuxFormat(): LuxFormat {
  const t = useT();
  const intl = useIntlShape();
  return useMemo(() => {
    const locale = intl.locale;
    return {
      t,
      locale,
      lux: (h) => fmtLux(h, locale),
      money: (minor, currency = "USD") => fmtMoney(minor, currency, locale),
      moneyShort: (minor, currency = "USD") => fmtMoneyShort(minor, currency, locale),
      date: (iso, withTime = false) =>
        !iso ? "—" : intl.formatDate(iso, withTime ? { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" } : { year: "numeric", month: "short", day: "numeric" }),
      pct: (whole) => intl.formatNumber(whole / 100, { style: "percent", maximumFractionDigits: 1 }),
      num: (n) => intl.formatNumber(n),
    };
  }, [t, intl]);
}

interface Api {
  /** Resolves true when the click may spend (included, confirmed, or confirmations switched off). */
  confirm: (req: SpendRequest, anchor?: Element | null) => Promise<boolean>;
  spent: (t: Omit<LuxToast, "id" | "kind">) => void;
  refunded: (t: Omit<LuxToast, "id" | "kind">) => void;
}

const Ctx = createContext<Api>({ confirm: async () => true, spent: () => undefined, refunded: () => undefined });

export function useLuxSpend(): Api {
  return useContext(Ctx);
}

interface Pending {
  req: SpendRequest;
  rect: DOMRect | null;
  resolve: (ok: boolean) => void;
}

export function LuxSpendProvider({ children }: { children: ReactNode }) {
  const me = useMe();
  const qc = useQueryClient();
  const pref = me.data?.preferences?.confirm_lux_spend;
  const prefRef = useRef(pref);
  prefRef.current = pref;
  const [pending, setPending] = useState<Pending | null>(null);
  const [toasts, setToasts] = useState<LuxToast[]>([]);
  const seq = useRef(0);
  const f = useLuxFormat();

  const confirm = useCallback((req: SpendRequest, anchor?: Element | null) => {
    if (!cannotPay(req) && !needsConfirm(req.cost, prefRef.current)) return Promise.resolve(true);
    return new Promise<boolean>((resolve) => setPending({ req, rect: anchor?.getBoundingClientRect() ?? null, resolve }));
  }, []);
  const push = useCallback((kind: LuxToast["kind"], t: Omit<LuxToast, "id" | "kind">) => {
    const id = ++seq.current;
    setToasts((list) => pushToast(list, { ...t, id, kind }));
    window.setTimeout(() => setToasts((list) => list.filter((x) => x.id !== id)), 7000);
    qc.invalidateQueries({ queryKey: ["lux"] });
    qc.invalidateQueries({ queryKey: ["lux-activity"] });
  }, [qc]);
  const value = useMemo<Api>(() => ({ confirm, spent: (t) => push("spent", t), refunded: (t) => push("refunded", t) }), [confirm, push]);

  const close = (ok: boolean, dontAsk = false) => {
    if (!pending) return;
    pending.resolve(ok);
    setPending(null);
    if (ok && dontAsk) {
      void unwrap(api.PATCH("/v1/me", { body: { confirm_lux_spend: false } })).then((m) => qc.setQueryData(["me"], m as S["Me"]));
    }
  };

  return (
    <Ctx.Provider value={value}>
      {children}
      {pending && <SpendPopover pending={pending} onClose={close} />}
      <div className="lux-toasts" aria-live="polite">
        {toasts.map((t) => {
          const text = toastText(t, f);
          return (
            <div key={t.id} className={`lux-toast is-${t.kind}`} role="status">
              <span className="lux-toast-mark"><LuxGlyph size={18} /></span>
              <span className="lux-toast-text">
                <b>{text.headline}</b>
                {text.detail && <span>{text.detail}</span>}
              </span>
              {t.href && <Link className="lux-toast-link" to={t.href} onClick={() => setToasts((l) => l.filter((x) => x.id !== t.id))}>{f.t("lux.toast.usageLog")}</Link>}
              <button type="button" className="lux-toast-close" aria-label={f.t("lux.toast.dismiss")} onClick={() => setToasts((l) => l.filter((x) => x.id !== t.id))}><X size={14} /></button>
            </div>
          );
        })}
      </div>
    </Ctx.Provider>
  );
}

/** The popover near the clicked button (centred when there is none). */
function SpendPopover({ pending, onClose }: { pending: Pending; onClose: (ok: boolean, dontAsk?: boolean) => void }) {
  const { req, rect } = pending;
  const [dontAsk, setDontAsk] = useState(false);
  const spendRef = useRef<HTMLButtonElement>(null);
  const out = cannotPay(req);
  const f = useLuxFormat();
  const { t } = f;
  const copy = confirmCopy(req, f);
  const navigate = useNavigate();
  const [leaving, setLeaving] = useState(false);
  const [saveError, setSaveError] = useState<{ section: "packs" | "plans"; message: string } | null>(null);
  // to billing: save the page's work first (beforeLeave), then go; checkout comes back to req.returnTo. A save that
  // fails keeps the user here with the reason and a Retry: unsaved work is never left behind.
  const go = async (section: "packs" | "plans") => {
    if (leaving) return;
    setLeaving(true);
    setSaveError(null);
    const ready = await prepareLeave(req.beforeLeave, f);
    setLeaving(false);
    if (!ready.ok) return setSaveError({ section, message: ready.error });
    onClose(false);
    navigate(buyLuxHref(req.returnTo, section));
  };
  const leave = (section: "packs" | "plans") => (e: ReactMouseEvent) => {
    e.preventDefault();
    void go(section);
  };
  useEffect(() => {
    spendRef.current?.focus();
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onClose(false);
      }
    };
    window.addEventListener("keydown", key, true);
    return () => window.removeEventListener("keydown", key, true);
  }, [onClose]);
  const width = 320;
  const style = rect
    ? (() => {
        const left = Math.max(12, Math.min(window.innerWidth - width - 12, rect.left + rect.width / 2 - width / 2));
        const below = rect.bottom + 10 + 230 < window.innerHeight;
        return below ? { left, top: rect.bottom + 10 } : { left, bottom: window.innerHeight - rect.top + 10 };
      })()
    : { left: "50%", top: "40%", transform: "translate(-50%, -50%)" };
  return (
    <>
      <div className="lux-spend-scrim" onClick={() => onClose(false)} />
      <div className="lux-spend" role="dialog" aria-modal="true" aria-labelledby="lux-spend-title" style={{ ...style, width }}>
        <div className="lux-spend-head">
          <span className="lux-spend-mark"><LuxGlyph size={20} /></span>
          <div>
            <b id="lux-spend-title">{out ? t("lux.spend.notEnough") : copy.title}</b>
            <span>
              {out
                ? req.balance != null
                  ? t("lux.spend.outHave", { label: req.label, cost: f.lux(req.cost), balance: f.lux(Math.max(req.balance, 0)) })
                  : t("lux.spend.outUnknown", { label: req.label, cost: f.lux(req.cost) })
                : copy.balance}
            </span>
            {out && req.returnTo && !saveError && <span className="lux-spend-worth">{t("lux.spend.comeBack")}</span>}
            {!out && copy.worth && <span className="lux-spend-worth">{copy.worth}</span>}
            {!out && req.note && <span className="lux-spend-worth lux-spend-note">{req.note}</span>}
          </div>
        </div>
        {saveError && (
          <div className="lux-spend-error" role="alert">
            <span>{saveError.message}</span>
            <button type="button" className="lux-spend-btn" onClick={() => void go(saveError.section)} disabled={leaving}>{leaving ? t("lux.spend.saving") : t("lux.spend.retry")}</button>
          </div>
        )}
        {out ? (
          <div className="lux-spend-actions">
            <button type="button" className="lux-spend-btn is-ghost" onClick={() => onClose(false)}>{t("lux.spend.notNow")}</button>
            <Link className="lux-spend-btn is-ghost" to={buyLuxHref(req.returnTo, "plans")} onClick={leave("plans")}>{t("lux.spend.upgrade")}</Link>
            <Link ref={spendRef as never} className="lux-spend-btn is-primary" to={buyLuxHref(req.returnTo, "packs")} onClick={leave("packs")} aria-busy={leaving || undefined}>{leaving ? t("lux.spend.saving") : t("lux.spend.buyLux")}</Link>
          </div>
        ) : (
          <>
            <label className="lux-spend-check">
              <input type="checkbox" checked={dontAsk} onChange={(e) => setDontAsk(e.target.checked)} />
              <span>
                {t("lux.spend.dontAsk")}
                <small>
                  <FormattedMessage id="lux.spend.changeIn" values={{ link: (chunks) => <Link to={SETTINGS_PATH} onClick={() => onClose(false)}>{chunks}</Link> }} />
                </small>
              </span>
            </label>
            <div className="lux-spend-actions">
              <button type="button" className="lux-spend-btn is-ghost" onClick={() => onClose(false)}>{t("lux.spend.cancel")}</button>
              <button type="button" ref={spendRef} className="lux-spend-btn is-primary" onClick={() => onClose(true, dontAsk)}>
                <LuxGlyph size={14} mono /> {copy.button}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
